import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Drawer — edge-anchored slide-over panel.
 *
 * A drawer rather than a Modal wherever the content is a record the user is
 * working through (a lead, a call, a queue member) and the list behind it is
 * still worth seeing.
 */

export type DrawerSide = "right" | "left" | "bottom";
export type DrawerWidth = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const WIDTHS: Record<DrawerWidth, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    // Charts and a 24-column heatmap. Narrower and the grid scrolls sideways.
    "3xl": "max-w-3xl",
};

interface DrawerProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    side?: DrawerSide;
    width?: DrawerWidth;
    closeOnBackdropClick?: boolean;
    /** Suppress Escape-to-close. Use when the drawer owns an unsaved form. */
    disableEscapeClose?: boolean;
    className?: string;
    "aria-label"?: string;
}

function edgeClasses(side: DrawerSide, width: DrawerWidth): string {
    if (side === "bottom") return "inset-x-0 bottom-0 max-h-[85vh] w-full rounded-t-panel";
    const anchor = side === "right" ? "right-0" : "left-0";
    return `top-0 bottom-0 ${anchor} w-full ${WIDTHS[width]}`;
}

export function Drawer({
    isOpen,
    onClose,
    children,
    side = "right",
    width = "lg",
    closeOnBackdropClick = true,
    disableEscapeClose = false,
    className = "",
    "aria-label": ariaLabel,
}: DrawerProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen || disableEscapeClose) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, disableEscapeClose, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        // The drawer scrolls its own body; letting the page scroll behind it
        // makes the list jump when the drawer closes.
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) panelRef.current?.focus();
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div
                className="absolute inset-0 bg-brand-ink/40 backdrop-blur-sm"
                onClick={closeOnBackdropClick ? onClose : undefined}
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                tabIndex={-1}
                className={`absolute flex flex-col bg-white shadow-panel outline-none ${edgeClasses(side, width)} ${className}`}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
}

interface DrawerHeaderProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    onClose?: () => void;
    actions?: React.ReactNode;
    className?: string;
}

function DrawerHeader({ title, subtitle, onClose, actions, className = "" }: DrawerHeaderProps) {
    const { t } = useTranslation("common");
    return (
        <div
            className={`flex flex-shrink-0 items-start justify-between gap-3 border-b border-brand-line bg-white px-5 py-3.5 ${className}`}
        >
            <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold text-brand-ink">{title}</h3>
                {subtitle && <div className="mt-0.5 text-xs text-brand-ink/60">{subtitle}</div>}
            </div>
            <div className="flex flex-shrink-0 items-center gap-0.5">
                {actions}
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1.5 text-brand-ink/40 transition-colors hover:bg-brand-cream hover:text-brand-ink"
                        aria-label={t("actions.close")}
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}
            </div>
        </div>
    );
}

function DrawerBody({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return <div className={`flex-1 overflow-y-auto bg-brand-cream/30 p-5 ${className}`}>{children}</div>;
}

function DrawerFooter({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <div
            className={`flex flex-shrink-0 items-center justify-end gap-2 border-t border-brand-line bg-white px-5 py-3 ${className}`}
        >
            {children}
        </div>
    );
}

Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;

export default Drawer;
